import type { ProvisioningStep } from '@sigora/domain';
import type { ReactNode, Ref } from 'react';

import type { PaymentProblem, Study, StudyPayment, StudyProvisioning } from '../api/client.ts';
import {
  CANCEL_CHECKOUT,
  CANCELLING_CHECKOUT,
  CHECKOUT_OPEN,
  CHECKOUT_OPEN_NO_FORM,
  CHECKOUT_OPENING,
  CHECKOUT_RETRY,
  CHECKOUT_WATCH_STOPPED,
  CHECK_PAYMENT_AGAIN,
  CHECKING_PAYMENT,
  checkoutAmountLine,
  NOT_CHARGED_TWICE,
  PAYMENT_CONFIRMING,
  PAYMENT_CONFIRMING_SLOW,
  PAYMENT_PROBLEM_COPY,
  PAYMENT_PROBLEMS_HEADING,
  problemRecordedLine,
  RETRY_SETUP,
  RETRYING_SETUP,
} from './launch-copy.ts';
import { priceLabel, priceTimeLabel } from './launch-format.ts';
import { PhaseStrip } from './PhaseStrip.tsx';

/** The requests Launch makes about a payment, one at a time; the one out names its button's label. */
export type PaymentAction = 'pay' | 'open' | 'cancel' | 'retry' | 'refresh';

/** A checkout request the server refused: its code, when it is one of the domain's, and its words. */
export interface CheckoutRefusal {
  code: string | null;
  message: string;
}

/**
 * The refusals that leave a pending checkout as it was and that a moment's wait clears: the
 * provider could not be reached, or the session is still being opened or briefly failed to be.
 * Every other refusal — the session gone, another provider's checkout, one refused and released —
 * is said with no Try again, since asking again answers the same.
 */
const RETRYABLE_CHECKOUT_CODES: ReadonlySet<string> = new Set([
  'checkout_opening',
  'payment_provider_unavailable',
]);

/** Whether a refusal is one asking for the checkout again can clear. */
export const isRetryableCheckoutRefusal = (refusal: CheckoutRefusal | null): boolean =>
  refusal?.code != null && RETRYABLE_CHECKOUT_CODES.has(refusal.code);

/**
 * What each set-up step is, in product words.
 *
 * The step names are the machine's, and every one of them is either a Remesh noun or an internal
 * verb — `create_conversation` and `go_live` say nothing to a researcher and `apply_audience` says
 * the wrong thing, since nothing is being applied to anybody. These are what the same eight steps
 * are from the outside, and the vendor doing the work is not named in any of them.
 *
 * Keyed by the whole enum rather than partially, so adding a ninth step to the order is a
 * typecheck failure here rather than a blank in the sentence the panel shows.
 */
const PROVISIONING_STEP_WORDING: Record<ProvisioningStep, string> = {
  create_conversation: 'Creating the study',
  set_metadata: 'Naming it',
  build_guide: 'Loading the discussion guide',
  apply_audience: 'Applying the audience',
  verify: 'Checking everything',
  lock: 'Locking the study',
  launch: 'Opening it to participants',
  go_live: 'Going live',
};

/** What is added when the page has stopped watching a run that has stopped. */
const POLLING_STOPPED =
  'This page has stopped checking on its own, so reload it after trying again.';

/**
 * What the panel says while a paid study is being set up and its run has not stopped.
 *
 * The having-waited sentence comes first, and it is a guess — the page's own count of unanswered
 * polls, which is all there is for a stop the server never recorded. A run that has a position
 * gets the step it is on, which is the only thing on this page that moves while eight sequential
 * calls are made; a paid study with no position yet is one whose job has not been picked up, and
 * it gets a sentence about the study rather than an invented "step 1".
 *
 * The total comes from the server rather than a literal eight: the list is the server's, and a
 * count typed here would be a second opinion able to disagree with the order being walked.
 */
export const settingUpLabel = ({
  provisioning,
  waitedTooLong,
}: {
  provisioning: StudyProvisioning | null;
  waitedTooLong: boolean;
}): string => {
  if (waitedTooLong) {
    return 'Paid for, and taking longer than it should to start. Nothing has been charged twice — reload to check again.';
  }

  return provisioning === null
    ? 'Paid for. Setting your study up — this takes a moment, and the page will say when it is live.'
    : `Setting up — step ${String(provisioning.index + 1)} of ${String(provisioning.total)}: ${PROVISIONING_STEP_WORDING[provisioning.step]}`;
};

/**
 * What the panel says about a run the server has stopped: the stop's own sentence, which the
 * server writes in product words and never with a vendor's message, and that nothing was charged
 * twice. The page's own giving-up is added rather than replacing it, since the restart is the one
 * control that still works and the poll that would show it working has stopped.
 */
export const stoppedLabel = ({
  stop,
  waitedTooLong,
}: {
  stop: { message: string };
  waitedTooLong: boolean;
}): string =>
  waitedTooLong
    ? `${stop.message} ${NOT_CHARGED_TWICE} ${POLLING_STOPPED}`
    : `${stop.message} ${NOT_CHARGED_TWICE}`;

/**
 * The study's payment problems that are still open: one card each, saying what happened and that
 * nothing is asked of the researcher. A settled one is not shown — its money has gone back.
 *
 * Rendered on every Launch view, because a problem outlives the purchase it is about: a payment
 * that arrived after its checkout closed leaves the study unpaid and ready to be paid for again.
 */
export const PaymentProblems = ({ problems }: { problems: readonly PaymentProblem[] }) => {
  const open = problems.filter((problem) => !problem.resolved);
  if (open.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="launch-problems-heading"
      className="launch-problems"
      data-test-id="launch-problems"
    >
      <h3 className="launch-problems-heading" id="launch-problems-heading">
        {PAYMENT_PROBLEMS_HEADING}
      </h3>
      {/*
        The status names no problem by id, and two problems of one kind can be recorded in the same
        instant, so the position in the server's newest-first list completes the key.
      */}
      {open.map((problem, index) => (
        <div
          className="launch-problem"
          data-test-id={`launch-problem-${problem.kind}`}
          key={`${problem.kind}:${problem.createdAt}:${String(index)}`}
        >
          <p>{PAYMENT_PROBLEM_COPY[problem.kind]}</p>
          <p className="launch-quote-detail">
            {problemRecordedLine(priceTimeLabel(problem.createdAt))}
          </p>
        </div>
      ))}
    </section>
  );
};

export interface LaunchPaymentProps {
  study: Study;
  /** The payment as last answered: a pending purchase, a paid one, or none on a locked study. */
  payment: StudyPayment | null;
  /** Whether a payment the form reported complete is being confirmed with the server. */
  confirming?: boolean;
  /**
   * Whether the quick checks on that payment have run out with it still unconfirmed. Cancel comes
   * back then — the server decides, and a payment that went through wins — beside Check again.
   */
  confirmingSlow?: boolean;
  /** Asks the server once more about a payment whose quick checks have run out. */
  onCheckAgain?: () => void;
  /**
   * What goes under an open checkout, where the payment form is mounted. Without it the checkout is
   * said to be open with no word of a form, and Cancel is the way out.
   */
  checkout?: ReactNode;
  /** The request out, if any; every control waits for it. */
  action: PaymentAction | null;
  /** Whether the page has stopped watching the set-up run. */
  waitedTooLong: boolean;
  /** Whether the page has stopped watching the open checkout. */
  watchStopped: boolean;
  /** The last checkout request the server refused, until another is made. */
  refusal: CheckoutRefusal | null;
  notice: string | null;
  onOpenCheckout: () => void;
  onCancel: () => void;
  onRetrySetup: () => void;
  /** The region, which takes focus when the control the researcher pressed goes away. */
  regionRef?: Ref<HTMLDivElement>;
}

/**
 * Launch once a purchase holds the study: its open checkout, and once paid, its set-up.
 *
 * Every sentence here is the server's status read back, never what the page last asked for: a
 * checkout is open because a session is bound, a study is being set up because the purchase is
 * paid, and a run is stopped because the server says nothing will run it again. One live region
 * carries the state's sentence and changes its words; refusals and a stopped run are alerts.
 */
export const LaunchPayment = ({
  study,
  payment,
  confirming = false,
  confirmingSlow = false,
  onCheckAgain,
  checkout,
  action,
  waitedTooLong,
  watchStopped,
  refusal,
  notice,
  onOpenCheckout,
  onCancel,
  onRetrySetup,
  regionRef,
}: LaunchPaymentProps) => {
  const pending = payment?.kind === 'pending' ? payment : null;
  const fulfillment = payment?.kind === 'paid' ? payment.fulfillment : null;
  const stop = fulfillment?.state === 'stopped' ? fulfillment.stop : null;
  const retryingStop = fulfillment?.state === 'retrying' ? fulfillment.stop : null;
  /**
   * A refunded payment is never set up again, and its card says so; a restart offered beside it
   * would be a button the server refuses.
   */
  const refunded =
    payment?.problems.some((problem) => problem.kind === 'refunded' && !problem.resolved) ?? false;
  const unavailable =
    pending !== null && (pending.checkout === 'opening' || isRetryableCheckoutRefusal(refusal));
  const busy = action !== null;

  return (
    <div
      aria-label="Payment"
      className="launch-payment"
      data-test-id="launch-payment"
      ref={regionRef}
      role="region"
      tabIndex={-1}
    >
      {pending === null && <PhaseStrip settingUp state={study.state} />}

      {pending !== null && (
        <p className="launch-quote-detail" data-test-id="launch-checkout-amount">
          {checkoutAmountLine(priceLabel(pending.purchase.quote))}
        </p>
      )}

      <p className="launch-payment-status" data-test-id="launch-payment-status" role="status">
        {pending !== null ? (
          confirming ? (
            <span data-test-id="launch-payment-confirming">
              {PAYMENT_CONFIRMING}
              {/* Only while something still checks: the open checkout's watch, until it stops. */}
              {confirmingSlow && !watchStopped && (
                <span data-test-id="launch-payment-confirming-slow">
                  {' '}
                  {PAYMENT_CONFIRMING_SLOW}
                </span>
              )}
            </span>
          ) : pending.checkout === 'open' ? (
            <span data-test-id="launch-checkout-open">
              {checkout === undefined ? CHECKOUT_OPEN_NO_FORM : CHECKOUT_OPEN}
            </span>
          ) : null
        ) : stop === null ? (
          <span data-test-id="launch-setting-up">
            {settingUpLabel({ provisioning: study.provisioning, waitedTooLong })}
          </span>
        ) : null}
      </p>

      {retryingStop !== null && (
        <p className="launch-quote-detail" data-test-id="launch-setup-retrying">
          {retryingStop.message}
        </p>
      )}

      {stop !== null && (
        <p className="chat-error" data-test-id="launch-setup-stopped" role="alert">
          {stoppedLabel({ stop, waitedTooLong })}
        </p>
      )}

      {/*
        Offered only against a stop the server has recorded. A run that is merely slow, or one the
        page has given up counting polls on, may still be working through a step, and a button
        that restarts a live run is a way to make a study worse than waiting does.
      */}
      {stop !== null && !refunded && (
        <button data-test-id="launch-retry" disabled={busy} onClick={onRetrySetup} type="button">
          {action === 'retry' ? RETRYING_SETUP : RETRY_SETUP}
        </button>
      )}

      {pending !== null && !confirming && checkout}

      {unavailable && (
        <>
          <p className="chat-error" data-test-id="launch-checkout-unavailable" role="alert">
            {refusal !== null && isRetryableCheckoutRefusal(refusal)
              ? refusal.message
              : CHECKOUT_OPENING}
          </p>
          <button
            data-test-id="launch-checkout-retry"
            disabled={busy}
            onClick={onOpenCheckout}
            type="button"
          >
            {CHECKOUT_RETRY}
          </button>
        </>
      )}

      {pending !== null && refusal !== null && !isRetryableCheckoutRefusal(refusal) && (
        <p className="chat-error" data-test-id="launch-payment-refused" role="alert">
          {refusal.message}
        </p>
      )}

      {pending !== null && confirming && confirmingSlow && onCheckAgain !== undefined && (
        <button
          data-test-id="launch-payment-check-again"
          disabled={busy}
          onClick={onCheckAgain}
          type="button"
        >
          {action === 'refresh' ? CHECKING_PAYMENT : CHECK_PAYMENT_AGAIN}
        </button>
      )}

      {pending !== null && (!confirming || confirmingSlow) && (
        <button
          data-test-id="launch-checkout-cancel"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          {action === 'cancel' ? CANCELLING_CHECKOUT : CANCEL_CHECKOUT}
        </button>
      )}

      {pending !== null && watchStopped && (
        <p data-test-id="launch-checkout-watch-stopped">{CHECKOUT_WATCH_STOPPED}</p>
      )}

      {notice !== null && <p data-test-id="launch-notice">{notice}</p>}
    </div>
  );
};
